import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import { Button } from "../../components/ui/Button";
import { api, extractError } from "../../lib/api";
import { useEinstufungProgress } from "../../context/EinstufungProgressContext";
import {
  ArrowLeft,
  Sparkle,
  CheckCircle,
  DownloadSimple,
  Warning,
  Info,
  Calendar,
  ChartLineUp,
} from "@phosphor-icons/react";
import { sumAnrechenbareMonate, formatDurationDays, hasUnresolvedQualityWarning } from "./shared";
import { isoToGerman } from "../../components/GermanDateInput";

/**
 * Iter 19.47.8 — Dienstzeit-Tile: drei-Zeilen-Stack mit Zahl-fett + Einheit-dezent.
 * Vermeidet hässliche Umbrüche bei „2 Jahre 6 Monate 22 Tage". Bei 0-Werten
 * wird die jeweilige Zeile ausgelassen. Bei komplett leerem Wert → "—".
 */
function DienstzeitTile({ dauer, fallback }) {
  const j = Number(dauer?.jahre ?? 0) || 0;
  const m = Number(dauer?.monate ?? 0) || 0;
  const t = Number(dauer?.tage ?? 0) || 0;
  const empty = j === 0 && m === 0 && t === 0;

  const rows = [
    [j, "Jahre"],
    [m, "Monate"],
    [t, "Tage"],
  ].filter(([val]) => val > 0);

  return (
    <div
      data-testid="tool-step4-dienstjahre"
      className="border border-ink-200 rounded-sm bg-white px-6 sm:px-8 py-6 flex flex-col"
    >
      <div className="text-[11px] uppercase tracking-widest font-semibold text-warm-500 mb-2">
        Anrechenbare Dienstzeit
      </div>
      <div className="flex-1 flex items-center">
        {empty ? (
          <div className="font-cabinet font-medium text-4xl text-ink-900">
            {fallback || "—"}
          </div>
        ) : (
          <div className="w-full space-y-1.5">
            {rows.map(([val, unit]) => (
              <div key={unit} className="flex items-baseline gap-2">
                <span className="font-cabinet font-medium text-3xl sm:text-4xl leading-none tracking-tight text-ink-900 tabular-nums w-[2ch] text-right">
                  {val}
                </span>
                <span className="text-[12px] uppercase tracking-widest font-semibold text-warm-500 leading-none pb-0.5">
                  {unit}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Iter 19.47.2 — Hero-Karte (heller, klare Trennung, ohne Header-Zeile).
 * Header-Labels „Finale Einstufung" und „ET · Empfehlung"-Badge entfernt.
 * Gruppen-Wert in dunklerem brand-Ton (brand-700), Stufe in brand-500 —
 * so harmoniert beides farblich, bleibt aber klar unterscheidbar.
 */
function HeroResult({ gruppe, stufe, hasStufenTabelle = true, stufenBeschreibung = "" }) {
  const g = (gruppe || "—").toString();
  // Iter 19.68.12 — KVs ohne Stufentabelle: „—" im Hero + Beschreibung als
  // Subtitle. Der LLM-Freitext ('nach 12 Verwendungsgruppenjahren') wandert
  // aus der 120px-Zelle in eine kleinere, wrap-fähige Subtitle-Zeile.
  const hasNoStufen = hasStufenTabelle === false;
  const rawStufe = (stufe || "").toString().trim();
  const s = hasNoStufen ? "—" : (rawStufe || "—");
  const subtitleText = (stufenBeschreibung || "").trim();
  // Iter 20.13 — Bei KVs mit semantischen Stufen-Labels (z. B. KV IT:
  // „Einstiegsstufe", „Regelstufe", „Erfahrungsstufe") muss die
  // Schriftgröße dynamisch skaliert werden — 120 px würde „Regelstufe"
  // aus dem Layout drücken. Für kurze Labels (≤ 4 Zeichen: „1", „c",
  // „III") behalten wir das große Display bei.
  const stufeLen = s.replace(/[\s.–\-]/g, "").length;
  const stufeIsShort = stufeLen <= 4;
  const stufeFontClass = stufeIsShort
    ? "text-[88px] sm:text-[120px]"
    : stufeLen <= 8
      ? "text-[48px] sm:text-[64px]"
      : "text-[32px] sm:text-[44px]";
  // Iter 20.36.f14.26 — Dynamische Font-Skalierung auch für Gruppe.
  // Bei KVs mit langen Gruppen-Labels (z. B. KV StB „BG II a)",
  // „BG III b)") wurde die 120-px-Version über 3 Zeilen umgebrochen
  // — analog zum bestehenden Muster für Stufe.
  const gruppeLen = g.replace(/[\s.–\-)(]/g, "").length;
  const gruppeFontClass = gruppeLen <= 3
    ? "text-[88px] sm:text-[120px]"
    : gruppeLen <= 6
      ? "text-[56px] sm:text-[76px]"
      : "text-[36px] sm:text-[52px]";
  return (
    <div
      data-testid="tool-step4-hero"
      className="relative overflow-hidden bg-warm-50 border border-ink-200 rounded-sm sm:col-span-2 flex flex-col min-h-[200px] animate-[fade-in-up_320ms_ease-out_both]"
    >
      {/* Brand accent line on top */}
      <div
        className="absolute top-0 left-0 h-[3px] w-full bg-brand-500"
        aria-hidden="true"
      />

      {/* Two-column display: GRUPPE | STUFE — divided by a thin vertical line */}
      <div className="flex-1 grid grid-cols-2 divide-x divide-ink-200">
        <div className="px-6 sm:px-8 py-6 flex flex-col min-w-0">
          <div className="text-[11px] uppercase tracking-widest font-semibold text-warm-500 mb-2">
            Gruppe
          </div>
          <div
            className={`font-cabinet font-medium ${gruppeFontClass} leading-none tracking-tight text-brand-700 break-words`}
            data-testid="tool-step4-hero-gruppe"
          >
            {g}
          </div>
        </div>
        <div className="px-6 sm:px-8 py-6 flex flex-col min-w-0">
          <div className="text-[11px] uppercase tracking-widest font-semibold text-warm-500 mb-2">
            Stufe
          </div>
          <div
            className={`font-cabinet font-medium ${stufeFontClass} leading-none tracking-tight text-brand-500 break-words`}
            data-testid="tool-step4-hero-stufe"
          >
            {s}
          </div>
          {subtitleText && (
            <div
              className="mt-3 text-sm leading-snug text-ink-600 font-normal max-w-full break-words"
              data-testid="tool-step4-hero-stufe-subtitle"
            >
              {subtitleText}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Iter 19.65.13 — Tile mit dem nächsten Vorrückungstermin (Promotion zur
 * nächsten KV-Stufe). Erscheint nur, wenn das Backend `next_vorrueckung`
 * liefert (KV hat Stufentabelle UND Mitarbeiter ist nicht bereits in der
 * höchsten Stufe).
 *
 * Iter 19.65.14 — Reduzierter Footprint: nur Label + Datum.
 * Iter 19.65.16/17 — Verlauf der nachfolgenden Stufen ist direkt in dieser
 * Karte integriert (nicht mehr als eigene Tile). Es werden nur die Stufen
 * NACH der „Nächsten Vorrückung" angezeigt (also `schedule.slice(1)`), um
 * Dopplungen zu vermeiden.
 * Datum-Logik: Y/M/D-Math + rückwirkende Vorrückung auf den Monatsersten
 * — siehe Backend `_compute_next_vorrueckung`.
 */
function NextVorrueckungTile({ next, schedule }) {
  if (!next || !next.date) return null;

  const isoToDe = (iso) => {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
    return m ? `${m[3]}.${m[2]}.${m[1]}` : iso;
  };
  const dateDe = isoToDe(next.date);

  // Nur weitere Stufen ab Index 1 (die nächste ist bereits oben sichtbar).
  const upcoming = Array.isArray(schedule) ? schedule.slice(1) : [];

  return (
    <div
      data-testid="tool-step4-next-vorrueckung"
      className="border border-ink-200 bg-white rounded-sm relative overflow-hidden animate-[fade-in-up_360ms_ease-out_both]"
    >
      <div
        className="absolute top-0 left-0 h-[3px] w-full bg-brand-400/60"
        aria-hidden="true"
      />
      {/* Hauptzeile — Nächste Vorrückung */}
      <div className="px-6 sm:px-8 py-5 flex items-center gap-4 sm:gap-6">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-sm border border-brand-300 bg-brand-50">
          <ChartLineUp size={18} weight="duotone" className="text-brand-600" />
        </div>
        <div className="text-[11px] uppercase tracking-widest font-semibold text-warm-500">
          Nächste Vorrückung
        </div>
        <div className="flex items-baseline gap-2 ml-auto">
          <Calendar size={14} weight="duotone" className="text-warm-500" />
          <span
            className="font-cabinet text-xl sm:text-2xl font-medium tracking-tight text-ink-900 tabular-nums"
            data-testid="tool-step4-next-vorrueckung-date"
          >
            {dateDe}
          </span>
        </div>
      </div>

      {/* Verlauf (kompakt, nachfolgende Stufen) */}
      {upcoming.length > 0 && (
        <ol
          data-testid="tool-step4-vorrueckung-schedule"
          className="border-t border-ink-100 px-6 sm:px-8 py-3 divide-y divide-ink-100/70"
        >
          {upcoming.map((item, idx) => (
            <li
              key={`${item.stufe}-${item.date}`}
              className="flex items-center gap-3 py-1.5 text-[13px]"
              data-testid={`tool-step4-schedule-row-${idx}`}
            >
              <span className="text-warm-600">
                Stufe{" "}
                <span className="font-cabinet font-medium text-ink-800">{item.stufe}</span>
              </span>
              <span className="flex-1 border-b border-dotted border-warm-200" />
              <span
                className="font-cabinet text-ink-800 tabular-nums"
                data-testid={`tool-step4-schedule-date-${idx}`}
              >
                {isoToDe(item.date)}
              </span>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

export default function StepFour({
  selectedKv,
  context,
  onBack,
  onReset,
}) {
  const [running, setRunning] = useState(false);
  const [error, setError] = useState("");
  const [rec, setRec] = useState(null);
  const [downloading, setDownloading] = useState(false);

  const { setProgress, registerPdfDownload, unregisterPdfDownload } = useEinstufungProgress();

  const totalAnrechenbar = sumAnrechenbareMonate(context.vordienstzeiten || [], context.stichtag);
  const totalAusb = (context.ausbildungszeiten || []).reduce(
    (s, r) => (r?.anrechenbar ? s + (Number(r.months) || 0) : s),
    0,
  );
  // Iter 19.63.19 — Quelle-of-Truth: Backend-Aggregate (gesetzt in Step 3).
  // Iter 19.63.28 — `aggregateHybridYMD`-FE-Fallback komplett entfernt.
  // Backend ist Single Source of Truth. Wenn `backendAggregate` (noch) leer
  // ist (z.B. User landet in Step 4 ohne Step 3 zu durchlaufen), zeigen wir
  // einen neutralen Fallback ("—") statt eine eigene FE-Math zu fahren.
  const vordienstAggregate = context.backendAggregate || {
    jahre: 0,
    monate: 0,
    tage: 0,
    totalDays: 0,
    display: "—",
  };
  // Iter 19.63.28 — totalAggregate (Vordienst + Ausbildung) brauchen wir nur
  // noch als Display-String für die Pre-Rec-Vorschau. Nach `setRec` wird
  // immer `rec.anrechenbare_dauer.display` (Backend) verwendet. Die
  // Pre-Rec-Anzeige nutzt jetzt einfache String-Komposition:
  // `vordienstAggregate.display` + ggf. Ausbildung-Hinweis daneben.
  const totalAnrechenbarDays = vordienstAggregate.totalDays || 0;
  const totalAusbDays = totalAusb * 30;

  const buildPayload = () => ({
    kv_id: selectedKv.id,
    consent_ack: true,
    suggested_group: context.suggestedGroup || "",
    taetigkeit_kurz: context.taetigkeitKurz || "",
    qualifikation: context.qualifikation || "",
    berufsjahre: context.berufsjahre || "",
    stichtag: context.stichtag || "",
    vordienstzeiten: (context.vordienstzeiten || []).map((v) => ({
      datum_von: v.datum_von || "",
      datum_bis: v.datum_bis || "",
      arbeitgeber: v.arbeitgeber || "",
      taetigkeit: v.taetigkeit || "",
      anrechenbar: Boolean(v.anrechenbar),
      // Iter 19.41.5 — Bug: ohne diesen Faktor halbierte das Backend
      // Hälftig-Items (Arbeiter mit 0.5) nicht und kam auf doppelte
      // Monatszahlen (z.B. 61 statt 30.7).
      anrechnungsfaktor:
        typeof v.anrechnungsfaktor === "number"
          ? v.anrechnungsfaktor
          : v.anrechenbar
          ? 1.0
          : 0.0,
      anrechenbarkeit_begruendung: v.anrechenbarkeit_begruendung || "",
      // Iter 19.63.21 — „laufend"-Flag durchreichen (Step 3 → Step 4 → PDF).
      was_laufend: Boolean(v.was_laufend),
      // Iter 19.63.29 — Authoritative Display-Strings vom Backend mitsenden,
      // damit der PDF-Renderer IDENTISCH zur UI rendert. Falls leer, fällt
      // der PDF-Endpoint auf eine interne Re-Berechnung via Pipeline zurück.
      display_raw: v.display_raw || "",
      display_anrechenbar: v.display_anrechenbar || "",
      // Iter 19.63.101 — Cap-Bypass-Flag (Step 3 → Step 4 → Final-Recommendation
      // → PDF). Backend `_apply_cap_to_items` überspringt diese Items vom
      // Cap-Walk; sie zählen ON TOP zur Höchstanrechnungsdauer.
      cap_bypass: Boolean(v.cap_bypass),
      // Iter 19.68.3 — Extraction-Quality-Flags + User-Acknowledgement
      // durchreichen. Backend leitet daraus die Liste der „ignorierten
      // Quality-Warnings" ab und rendert diese im PDF-Audit-Block. Ohne
      // diese Felder hätte der PDF-Pfad keine Info über bestätigte/
      // ungeprüfte Positionen.
      _quality_flags: Array.isArray(v._quality_flags) ? v._quality_flags : [],
      _quality_ack: v._quality_ack === true,
      // Iter 19.68.8 — Konsolidierungs-Lücken-Tage über Step-3→Step-4-
      // Roundtrip erhalten (siehe Iter 19.68.6 Bug-Repro).
      _consolidation_gap_days:
        typeof v._consolidation_gap_days === "number"
          ? v._consolidation_gap_days
          : 0,
    })),
    ausbildungszeiten: (context.ausbildungszeiten || [])
      .filter((r) => r && r.label && r.label !== "__manual__" && (Number(r.months) || 0) > 0)
      .map((r) => ({
        label: r.label,
        months: Number(r.months) || 0,
        anrechenbar: Boolean(r.anrechenbar),
        begruendung: r.begruendung || "",
        ai_matched: Boolean(r.ai_matched),
        bypass_cap: Boolean(r.bypass_cap),
      })),
  });

  const runRecommendation = async () => {
    setError("");
    setRunning(true);
    try {
      const { data } = await api.post(
        "/tool/final-recommendation",
        buildPayload(),
        { timeout: 120_000 },
      );
      const r = data?.recommendation;
      if (!r) throw new Error("Unerwartete Antwort vom System.");
      setRec(r);
      // Iter 19.42.4 — Mergen der vom Backend nach Overlap-Resolution + Cap
      // aktualisierten `effective_days_anrechenbar` pro Item in den State.
      // Damit zeigt die Sidebar (Schritt 3) nach Klick auf "Empfehlung erstellen"
      // exakt den gleichen Wert wie die Empfehlungs-Karte (Schritt 4).
      const itemsUpdated = r.items_updated;
      if (Array.isArray(itemsUpdated) && itemsUpdated.length === (context.vordienstzeiten || []).length) {
        const updated = context.vordienstzeiten.map((local, i) => {
          const fromBackend = itemsUpdated[i];
          if (!fromBackend) return local;
          // Sicherheits-Check: nur mergen wenn datum_von + datum_bis übereinstimmen
          // (sonst Item-Reihenfolge anders → besser keine Werte überschreiben).
          if (fromBackend.datum_von !== local.datum_von || fromBackend.datum_bis !== local.datum_bis) {
            return local;
          }
          return {
            ...local,
            effective_days_anrechenbar: fromBackend.effective_days_anrechenbar,
            months_anrechenbar: fromBackend.months_anrechenbar,
            dauer_jahre: fromBackend.dauer_jahre,
            dauer_monate: fromBackend.dauer_monate,
            dauer_tage: fromBackend.dauer_tage,
            // Iter 19.43.4 — `effective_period` aus Backend übernehmen.
            effective_period: fromBackend.effective_period || undefined,
            // Iter 19.61.2 — Single Source of Truth: per-Item Y/M/T vom Backend.
            dauer_anrechenbar_jahre:
              typeof fromBackend.dauer_anrechenbar_jahre === "number"
                ? fromBackend.dauer_anrechenbar_jahre
                : local.dauer_anrechenbar_jahre,
            dauer_anrechenbar_monate:
              typeof fromBackend.dauer_anrechenbar_monate === "number"
                ? fromBackend.dauer_anrechenbar_monate
                : local.dauer_anrechenbar_monate,
            dauer_anrechenbar_tage:
              typeof fromBackend.dauer_anrechenbar_tage === "number"
                ? fromBackend.dauer_anrechenbar_tage
                : local.dauer_anrechenbar_tage,
          };
        });
        if (typeof context.setVordienstzeiten === "function") {
          context.setVordienstzeiten(updated);
        }
      }
      // Iter 19.43.5 — Backend-Aggregate aus final-recommendation in Sidebar
      // pushen, damit Sidebar = Empfehlungs-Karte = identisch.
      // Iter 19.63.13 / 19.63.37 — Konsistenz mit Sidebar/Modal-Math.
      //
      // Backend `r.anrechenbare_dauer` ist das FINAL-Total inkl.:
      //   1) Vordienstzeiten (Backend-gecappt)
      //   2) Normale Bonuszeiten (mit Vordienst zusammen gecappt)
      //   3) Bypass-Bonuszeiten (nach Cap addiert)
      //
      // Damit `backendAggregate.totalDays` weiterhin nur die reinen Vordienst-
      // Days repräsentiert (und der useEffect in EinstufungToolPage on-the-fly
      // wieder Bonus-Days addieren kann ohne Double-Counting), subtrahieren wir
      // BEIDE Bonus-Anteile vom Final-Total.
      //
      // Iter 19.63.37 Fix: Vorher wurde NUR bypass subtrahiert → normaler
      // Bonus wurde implizit beim Zurück-Klick verdoppelt (Empfehlung schreibt
      // 922+720=1642 zurück, useEffect addiert nochmal 720 → 2362). Test:
      // Jacqui Fall 2 + HAK-Matura + Zurück + Vorwärts → konsistent.
      if (typeof context.setBackendAggregate === "function" && r.anrechenbare_dauer) {
        const _activeAusb = (context.ausbildungszeiten || []).filter(
          (a) => a && a.anrechenbar && (Number(a.months) || 0) > 0
        );
        const normalBonusMonths = _activeAusb
          .filter((a) => !a.bypass_cap)
          .reduce((sum, a) => sum + Number(a.months), 0);
        const bypassMonths = _activeAusb
          .filter((a) => a.bypass_cap)
          .reduce((sum, a) => sum + Number(a.months), 0);
        // Iter 19.63.19 — Source-of-Truth: r.anrechenbare_dauer (exakte Y/M/T-
        // Integer vom Backend) → totalDays direkt. NICHT mehr über
        // r.anrechenbare_monate (1-Dezimal-Float) gehen — das verliert
        // 0.5–1 Tag durch float→round→×30.
        const finalY = Number(r.anrechenbare_dauer.jahre || 0);
        const finalM = Number(r.anrechenbare_dauer.monate || 0);
        const finalT = Number(r.anrechenbare_dauer.tage || 0);
        const finalTotalDays = finalY * 360 + finalM * 30 + finalT;
        const normalBonusDays = normalBonusMonths * 30;
        const bypassDays = bypassMonths * 30;
        const vorienstOnlyDays = Math.max(
          0,
          finalTotalDays - bypassDays - normalBonusDays,
        );
        // Re-cascade Y/M/T from the Vordienst-only days so the Sidebar can
        // re-add the bonus independently.
        const y = Math.floor(vorienstOnlyDays / 360);
        const m = Math.floor((vorienstOnlyDays % 360) / 30);
        const t = vorienstOnlyDays % 30;
        const parts = [];
        if (y) parts.push(`${y} J.`);
        if (m) parts.push(`${m} M.`);
        if (t || (!y && !m)) parts.push(`${t} T.`);
        context.setBackendAggregate({
          display: parts.join(" "),
          jahre: y,
          monate: m,
          tage: t,
          totalDays: vorienstOnlyDays,
        });
      }
      toast.success(
        `Empfehlung erstellt: Beschäftigungsgruppe ${r.beschaeftigungsgruppe || "—"}.`,
      );
    } catch (err) {
      setError(extractError(err, "Empfehlung konnte nicht erstellt werden.", { aiContext: true }));
    } finally {
      setRunning(false);
    }
  };

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      // Iter 19.41.2: Den im UI bereits erstellten Empfehlungs-Datensatz
      // mit-senden — damit das Backend keine neue LLM-Generierung anstößt
      // und die Formulierungen 1:1 mit der Anzeige übereinstimmen.
      const payload = rec
        ? { ...buildPayload(), recommendation: rec }
        : buildPayload();
      const resp = await api.post(
        "/tool/final-recommendation/pdf",
        payload,
        { responseType: "blob", timeout: 120_000 },
      );
      const blob = new Blob([resp.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "ET-Einstufungs-Empfehlung.pdf";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast.success("PDF heruntergeladen — keine Kopie auf dem Server.");
    } catch (err) {
      // For Blob errors, the error body is a Blob — try to read it as JSON
      let msg = "PDF-Download fehlgeschlagen.";
      try {
        const blob = err?.response?.data;
        if (blob && blob.text) {
          const text = await blob.text();
          const json = JSON.parse(text);
          if (json.detail) msg = json.detail;
        } else {
          msg = extractError(err, msg);
        }
      } catch {
        msg = extractError(err, msg);
      }
      toast.error(msg);
    } finally {
      setDownloading(false);
    }
  };

  // Push the final recommendation into the sidebar Wizard-Live-View as
  // soon as it's available, and tear it down when this step unmounts.
  useEffect(() => {
    if (rec) {
      setProgress({
        finalGroup: rec.beschaeftigungsgruppe || null,
        finalStufe: rec.gehaltsstufe ?? null,
      });
    }
  }, [rec, setProgress]);

  // Register the PDF-download callback with the sidebar so the
  // Empfehlung-card can offer a one-click shortcut.
  useEffect(() => {
    if (rec) {
      registerPdfDownload(downloadPdf, downloading);
    }
    return () => {
      unregisterPdfDownload();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rec, downloading]);

  // Iter 20.04 — Architektur-Refactor: Schritt 4 ist jetzt eine reine
  // Ergebnis-Ansicht. Die Empfehlung wird automatisch beim Mount getriggert,
  // sobald der User in Schritt 3 auf „Empfehlung erstellen" klickt.
  // Kein „vorher"-Screen mit Zusammenfassung + Button mehr — das ist
  // redundant zur Live-View in Schritt 3.
  useEffect(() => {
    if (!rec && !running && !error) {
      runRecommendation();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Error state — Empfehlung konnte nicht erstellt werden. Bietet Retry
  // und Zurück-Button, damit User nicht in einer Sackgasse landet.
  if (!rec && !running && error) {
    return (
      <section
        className="border border-ink-200 bg-white rounded-sm relative"
        data-testid="tool-step-4-error"
      >
        <div className="absolute top-0 left-0 h-1 w-24 bg-status-abgelehnt-border" aria-hidden="true" />

        <div className="px-8 py-8 border-b border-ink-200 flex items-start gap-4">
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-sm border border-status-abgelehnt-border bg-status-abgelehnt-bg">
            <Warning size={22} weight="duotone" className="text-status-abgelehnt-text" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-widest font-semibold text-warm-500 mb-1">
              Schritt 4 von 4
            </div>
            <h2 className="font-cabinet text-2xl tracking-tight text-ink-900 font-medium leading-tight">
              Empfehlung konnte nicht erstellt werden
            </h2>
            <p className="mt-2 text-sm text-warm-700 leading-relaxed">{error}</p>
          </div>
        </div>

        <div className="px-8 py-5 border-t border-ink-200 flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-3 bg-ink-50">
          <Button variant="secondary" size="lg" onClick={onBack} data-testid="tool-step4-back">
            <ArrowLeft size={14} weight="bold" />
            Zurück zu Schritt 3
          </Button>
          <Button
            variant="brand"
            size="lg"
            onClick={runRecommendation}
            data-testid="tool-step4-retry"
          >
            <Sparkle size={14} weight="bold" />
            Erneut versuchen
          </Button>
        </div>
      </section>
    );
  }

  // Loading state — beim initialen Mount (bevor useEffect greift) oder
  // während des API-Calls. Beides zeigt den gleichen Loading-Screen.
  // Iter 20.36.f14.15 — Container-Höhe an Schritt-2-Loading angeglichen
  // (min-h-[600px] + flex-col + flex-1 vertikal zentriert), damit beim
  // Übergang zum Ergebnis-Block kein Sprung von kleiner Box → großer
  // Ergebnis-Block entsteht.
  if (running || (!rec && !error)) {
    return (
      <section
        className="border border-ink-200 bg-white rounded-sm relative overflow-hidden min-h-[600px] flex flex-col"
        data-testid="tool-step-4-loading"
      >
        <div className="loading-bar-brand absolute top-0 left-0 right-0 h-[3px]" aria-hidden="true" />
        <div className="flex-1 px-8 py-16 text-center flex flex-col items-center justify-center">
          <div className="mx-auto mb-6 flex items-center justify-center">
            <span className="relative inline-flex h-16 w-16 items-center justify-center" aria-hidden="true">
              <span className="absolute inset-0 rounded-full bg-brand-400/30 animate-ping" />
              <span
                className="absolute inset-1 rounded-full bg-brand-400/20"
                style={{ animation: "et-loading-pulse 1.8s ease-in-out infinite" }}
              />
              <img
                src="/et-mark-dark.png"
                alt="ET"
                className="relative h-12 w-auto"
                style={{ animation: "et-loading-bob 2.4s ease-in-out infinite" }}
                draggable={false}
              />
            </span>
          </div>
          <div className="font-cabinet text-2xl tracking-tight text-ink-900 font-medium leading-tight">
            Empfehlung wird erstellt…
          </div>
          <div className="mt-2 text-sm text-warm-600">
            ET gleicht Tätigkeit, Qualifikation und Vordienstzeiten mit dem KV-Regelwerk ab.
          </div>
          <div className="mt-7 mx-auto w-full max-w-md h-[2px] loading-bar-brand" />
          <div className="mt-3 inline-flex items-center gap-2" aria-hidden="true">
            <span className="brand-dot" />
            <span className="brand-dot" />
            <span className="brand-dot" />
          </div>
        </div>
      </section>
    );
  }

  // Result state
  return (
    <section
      className="border border-ink-200 bg-white rounded-sm relative"
      data-testid="tool-step-4-result"
    >
      <div
        className="absolute top-0 left-0 h-1 w-32 bg-status-aktiv-border"
        aria-hidden="true"
      />

      <div className="px-8 py-8 border-b border-ink-200 flex items-start gap-4">
        <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-sm border border-status-aktiv-border bg-status-aktiv-bg">
          <CheckCircle size={22} weight="duotone" className="text-status-aktiv-text" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-widest font-semibold text-warm-500 mb-1">
            Finale Empfehlung
            {context.stichtag && (
              <>
                {" · Stichtag "}
                <span className="text-ink-900" data-testid="tool-step4-result-stichtag">
                  {new Date(context.stichtag).toLocaleDateString("de-AT")}
                </span>
              </>
            )}
          </div>
          <h2 className="font-cabinet text-2xl tracking-tight text-ink-900 font-medium leading-tight">
            Einstufung gemäß <span className="text-brand-500">{selectedKv.name}</span>
          </h2>
        </div>
      </div>

      <div className="px-8 py-7 space-y-7">
        {/* Core grid — Hero (BG + Stufe combined) + Dienstzeit-Side-Card */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <HeroResult
            gruppe={rec.beschaeftigungsgruppe}
            stufe={rec.gehaltsstufe}
            hasStufenTabelle={rec.has_stufen_tabelle !== false}
            stufenBeschreibung={rec.stufen_beschreibung || ""}
          />
          <DienstzeitTile
            dauer={rec.anrechenbare_dauer}
            fallback={rec.anrechenbare_dienstjahre}
          />
        </div>

        {/* Iter 19.65.13 / 19.65.16-17 — Nächster Vorrückungstermin
            inkl. integriertem Verlauf der nachfolgenden Stufen. */}
        <NextVorrueckungTile
          next={rec.next_vorrueckung}
          schedule={rec.vorrueckung_schedule}
        />

        {/* Mitarbeiter-Kontext (mirrors the PDF block of the same name) */}
        <div data-testid="tool-step4-context-block">
          <div className="text-[10px] uppercase tracking-widest font-semibold text-warm-500 mb-2">
            Mitarbeiter-Kontext
          </div>
          <div className="border border-ink-200 rounded-sm overflow-hidden">
            <dl className="divide-y divide-ink-200">
              {/* Tätigkeit */}
              <div
                className="grid grid-cols-1 sm:grid-cols-[180px_1fr] gap-x-6 gap-y-1 px-4 py-2.5 text-sm"
              >
                <dt className="text-[11px] uppercase tracking-wide text-warm-500 font-semibold sm:pt-0.5">
                  Tätigkeit
                </dt>
                <dd className="text-ink-900 whitespace-pre-line">
                  {context.taetigkeitKurz || <span className="text-warm-400">—</span>}
                </dd>
              </div>

              {/* Gruppe — Wert + textuelle Beschreibung + Abweichungs-Hinweis */}
              <div
                className="grid grid-cols-1 sm:grid-cols-[180px_1fr] gap-x-6 gap-y-1 px-4 py-2.5 text-sm"
                data-testid="tool-step4-ctx-gruppe"
              >
                <dt className="text-[11px] uppercase tracking-wide text-warm-500 font-semibold sm:pt-0.5">
                  Gruppe
                </dt>
                <dd className="text-ink-900 space-y-1">
                  <div className="font-medium">
                    {rec.beschaeftigungsgruppe || <span className="text-warm-400">—</span>}
                  </div>
                  {rec.gruppe_beschreibung && (
                    <div className="text-[13px] text-warm-700 leading-relaxed">
                      {rec.gruppe_beschreibung}
                    </div>
                  )}
                </dd>
              </div>
            </dl>
          </div>
        </div>

        {/* Vordienstzeiten (mirrors the PDF table) */}
        <div data-testid="tool-step4-vordienstzeiten-block">
          {/* Iter 19.68.3 — Warning-Banner: ungeprüfte Quality-Flags
              werden in Schritt 4 sichtbar markiert, damit der User es
              im letzten Moment sieht (Audit-Trail-Schutz: niemand kann
              später behaupten, ET hätte nicht gewarnt). */}
          {(() => {
            const unresolved = (context.vordienstzeiten || [])
              .map((v, i) => ({ row: v, idx: i }))
              .filter(({ row }) => hasUnresolvedQualityWarning(row));
            if (unresolved.length === 0) return null;
            return (
              <div
                data-testid="tool-step4-quality-warning-banner"
                className="mb-3 flex items-start gap-2.5 border border-amber-400 bg-amber-50 px-3 py-2.5 rounded-sm text-[12px] text-amber-900 leading-snug"
              >
                <svg className="shrink-0 mt-0.5" width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <path d="M12 2L1 21h22L12 2zm0 6l7.53 13H4.47L12 8zm-1 4v4h2v-4h-2zm0 6v2h2v-2h-2z"/>
                </svg>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold mb-0.5">
                    {unresolved.length === 1
                      ? "1 ungeprüfte Auslese-Unsicherheit in der Empfehlung"
                      : `${unresolved.length} ungeprüfte Auslese-Unsicherheiten in der Empfehlung`}
                  </div>
                  Die folgende Empfehlung enthält Vordienstzeiten, bei denen
                  ET sich beim Auslesen nicht sicher war und die nicht
                  bestätigt wurden. Diese Positionen werden im PDF-Export
                  als ungeprüft markiert.
                </div>
              </div>
            );
          })()}
          <div className="flex items-baseline justify-between mb-2 gap-3">
            <div className="text-[10px] uppercase tracking-widest font-semibold text-warm-500">
              Vordienstzeiten
            </div>
            {(context.vordienstzeiten?.length > 0 || totalAusb > 0) && (
              <div
                className="text-[11px] text-warm-700 flex items-center gap-1.5"
                data-testid="tool-step4-vordienstzeiten-sum"
              >
                Summe anrechenbar:{" "}
                <span className="font-semibold text-brand-700">
                  {/* Iter 19.63.28: Backend-`rec.anrechenbare_dauer.display`
                      (gesetzt nach final-recommendation) → Fallback auf
                      `vordienstAggregate.display` (Backend-Step-3-Aggregate).
                      Keine FE-Math mehr. */}
                  {rec.anrechenbare_dauer?.display ||
                   rec.anrechenbare_dienstjahre ||
                   vordienstAggregate.display}
                </span>
                {totalAusb > 0 && (totalAnrechenbar + totalAusb) <= (selectedKv?.cap_months || Infinity) && (
                  <span className="text-warm-500">
                    (davon {formatDurationDays(totalAusbDays)} ausbildungsbedingt)
                  </span>
                )}
                {(totalAnrechenbar + totalAusb) > (selectedKv?.cap_months || Infinity) && (
                  <span
                    className="inline-flex items-center text-warm-500"
                    title={
                      `Höchstanrechnungsdauer (${Math.round((selectedKv?.cap_months || 0) / 12)} J. lt. KV) greift — ` +
                      `Brutto-Summe wäre ${formatDurationDays(totalAnrechenbarDays + totalAusbDays)} ` +
                      (totalAusb > 0 ? `(davon ${formatDurationDays(totalAusbDays)} ausbildungsbedingt). ` : `. `) +
                      `Details siehe Begründung unten.`
                    }
                    data-testid="tool-step4-cap-info"
                  >
                    <Info size={13} weight="bold" />
                  </span>
                )}
                {/* Iter 19.42.3 — Schritt 3, Schritt 4-vor und Schritt 4-nach
                    nutzen jetzt den gleichen Datenpfad: Frontend bevorzugt das
                    backend-präzise `effective_days_anrechenbar` (mit Overlap-
                    Resolution), fällt aber bei User-Edits auf lokale Schätzung
                    zurück. Daher entfällt der frühere Drift-Hinweis. */}
              </div>
            )}
          </div>
          {(context.vordienstzeiten?.length > 0 || (context.ausbildungszeiten || []).some((r) => r && r.label && (Number(r.months) || 0) > 0)) ? (
            <div className="border border-ink-200 rounded-sm overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-warm-50 text-[11px] uppercase tracking-wide text-warm-500 font-semibold">
                  <tr>
                    <th className="px-3 py-2 text-left w-[22%]">Zeitraum</th>
                    <th className="px-3 py-2 text-left">Arbeitgeber / Tätigkeit</th>
                    <th className="px-3 py-2 text-left w-[12%]">Dauer</th>
                    <th className="px-3 py-2 text-left w-[10%]">Anrechenbar</th>
                    <th className="px-3 py-2 text-left w-[30%]">Begründung</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-ink-200">
                  {/* Ausbildungs-rows first (chronologically pre-employment), styled
                      identically to the PDF: light indigo background + "(fiktiv)" label. */}
                  {(context.ausbildungszeiten || [])
                    .filter((r) => r && r.label && r.label !== "__manual__" && (Number(r.months) || 0) > 0)
                    .map((a, i) => {
                      const months = Number(a.months) || 0;
                      const yearsPart = months / 12;
                      const durStr =
                        Math.abs(yearsPart - Math.round(yearsPart)) < 0.05
                          ? `+${Math.round(yearsPart)} J.`
                          : `+${Math.round(months)} M.`;
                      return (
                        <tr
                          key={`ausb-${i}`}
                          className="align-top bg-[#EEF2FF]"
                          data-testid={`tool-step4-ausb-row-${i}`}
                        >
                          <td className="px-3 py-2.5 text-warm-500 italic whitespace-nowrap">
                            —
                          </td>
                          <td className="px-3 py-2.5">
                            <div className="font-medium text-ink-900">
                              <span className="text-[10px] uppercase tracking-wider font-semibold text-indigo-700 bg-indigo-100 border border-indigo-200 px-1.5 py-0.5 rounded-sm mr-2">
                                fiktiv
                              </span>
                              {a.label}
                            </div>
                            <div className="text-warm-600 text-xs mt-0.5 leading-snug">
                              Ausbildungsbedingte Anrechnung
                            </div>
                          </td>
                          <td className="px-3 py-2.5 text-ink-900 whitespace-nowrap tabular-nums font-medium">
                            {durStr}
                          </td>
                          <td className="px-3 py-2.5">
                            <span
                              className={[
                                "inline-flex items-center px-2 py-0.5 rounded-sm text-[11px] font-semibold uppercase tracking-wide",
                                a.anrechenbar
                                  ? "bg-status-aktiv-bg text-status-aktiv-text border border-status-aktiv-border"
                                  : "bg-status-abgelehnt-bg text-status-abgelehnt-text border border-status-abgelehnt-border",
                              ].join(" ")}
                            >
                              {a.anrechenbar ? "Ja" : "Nein"}
                            </span>
                          </td>
                          <td className="px-3 py-2.5 text-ink-900 leading-snug">
                            {a.begruendung || (
                              <span className="text-warm-400">—</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  {context.vordienstzeiten.map((v, i) => {
                    const von = isoToGerman(v.datum_von) || "—";
                    const bis = isoToGerman(v.datum_bis) || "laufend";
                    const isUnreviewedQuality = hasUnresolvedQualityWarning(v);
                    return (
                      <tr
                        key={i}
                        className={[
                          "align-top",
                          isUnreviewedQuality
                            ? "bg-amber-50 border-l-4 border-l-amber-500"
                            : v.anrechenbar
                            ? "bg-[#F0FDF4]"
                            : "bg-[#FEF2F2]",
                        ].join(" ")}
                        data-testid={`tool-step4-vd-row-${i}`}
                        data-unreviewed-quality={isUnreviewedQuality ? "true" : undefined}
                      >
                        <td className="px-3 py-2.5 text-ink-900 whitespace-nowrap">
                          {von} – {bis}
                          {isUnreviewedQuality && (
                            <div
                              className="mt-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm border border-amber-400 bg-white text-amber-800 text-[9.5px] font-medium uppercase tracking-wider"
                              data-testid={`tool-step4-vd-row-${i}-unreviewed-badge`}
                              title="ET war sich beim Auslesen dieser Position nicht sicher und sie wurde nicht bestätigt."
                            >
                              ungeprüft
                            </div>
                          )}
                        </td>
                        <td className="px-3 py-2.5">
                          <div className="font-medium text-ink-900">
                            {v.arbeitgeber || <span className="text-warm-400">—</span>}
                          </div>
                          {v.taetigkeit && (
                            <div className="text-warm-600 text-xs mt-0.5 leading-snug">
                              {v.taetigkeit}
                            </div>
                          )}
                        </td>
                        <td className="px-3 py-2.5 text-ink-900 whitespace-nowrap">
                          {/* Iter 20.36.f14.15 — Dauer-Spalte spiegelt PDF:
                              display_anrechenbar als Hauptwert, display_raw
                              als grauer Subtitel wenn beide unterschiedlich
                              (Overlap/Cap/Faktor haben reduziert). */}
                          {(() => {
                            const dispAnr = (v.display_anrechenbar || "").trim();
                            const dispRaw = (v.display_raw || "").trim();
                            if (!dispAnr && !dispRaw) {
                              return <span className="text-warm-400">—</span>;
                            }
                            const isReduced = dispRaw && dispAnr && dispRaw !== dispAnr;
                            return (
                              <>
                                <div className="tabular-nums font-medium">
                                  {dispAnr || dispRaw}
                                </div>
                                {isReduced && (
                                  <div className="text-warm-500 text-[10.5px] tabular-nums mt-0.5">
                                    ({dispRaw} roh)
                                  </div>
                                )}
                              </>
                            );
                          })()}
                        </td>
                        <td className="px-3 py-2.5">
                          <span
                            className={[
                              "inline-flex items-center px-2 py-0.5 rounded-sm text-[11px] font-semibold uppercase tracking-wide",
                              v.anrechenbar
                                ? "bg-status-aktiv-bg text-status-aktiv-text border border-status-aktiv-border"
                                : "bg-status-abgelehnt-bg text-status-abgelehnt-text border border-status-abgelehnt-border",
                            ].join(" ")}
                          >
                            {v.anrechenbar ? "Ja" : "Nein"}
                          </span>
                        </td>
                        <td className="px-3 py-2.5 text-ink-900 leading-snug">
                          {v.anrechenbarkeit_begruendung || (
                            <span className="text-warm-400">—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-sm text-warm-500 italic">
              Keine Vordienstzeiten angegeben.
            </p>
          )}
        </div>

        {rec.begruendung && (
          <div data-testid="tool-step4-begruendung">
            <div className="text-[10px] uppercase tracking-widest font-semibold text-warm-500 mb-2">
              Begründung
            </div>
            <p className="text-sm text-ink-900 leading-relaxed whitespace-pre-line">
              {rec.begruendung}
            </p>
          </div>
        )}

        {rec.hinweise?.length > 0 && (
          <div data-testid="tool-step4-hinweise">
            <div className="text-[10px] uppercase tracking-widest font-semibold text-warm-500 mb-2">
              Hinweise
            </div>
            <ul className="space-y-2">
              {rec.hinweise.map((h, i) => (
                <li
                  key={i}
                  data-testid={`tool-step4-hinweis-${i}`}
                  className="flex items-start gap-2 text-sm text-warm-700"
                >
                  <Info size={14} weight="bold" className="mt-0.5 shrink-0 text-brand-500" />
                  <span>{h}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      <div className="px-8 py-5 border-t border-ink-200 flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-3 bg-ink-50">
        <Button variant="secondary" size="lg" onClick={onReset} data-testid="tool-step4-restart">
          <Calendar size={14} weight="bold" />
          Neue Einstufung starten
        </Button>
        <div className="flex items-center gap-3 flex-wrap justify-end">
          <Button
            variant="secondary"
            size="md"
            onClick={onBack}
            data-testid="tool-step4-back-from-result"
          >
            <ArrowLeft size={14} weight="bold" />
            Zurück
          </Button>
          <Button
            variant="brand"
            size="lg"
            onClick={downloadPdf}
            disabled={downloading}
            data-testid="tool-step4-download-pdf"
          >
            <DownloadSimple size={14} weight="bold" />
            {downloading ? "Generiere PDF…" : "Bericht als PDF herunterladen"}
          </Button>
        </div>
      </div>
    </section>
  );
}
